'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import SiteNav from './nav/SiteNav';
import { NAV_SEARCH } from './nav/styles';
import GameCover from './GameCover';
import { GAMES, gamesForWith, GROUP_LABEL, isNew, PAYS_KHARMA, type Game, type GameOverrides } from '@/lib/games';
import { PLAY_AFTER_MS, popularityRank, recordPlay, topPlayed, useGamePlays } from '@/lib/gamePlays';
import { useGamePresence } from '@/lib/gamePresence';
import GameShot from './GameShot';
import GameLoader, { type LoadPhase } from './GameLoader';
import { S_BTN_PRIMARY } from './settingsUi';
import { useTheme } from '@/lib/theme';
import { useAuth } from '@/lib/auth';
import { BRAND } from '@/lib/brand';
import { rankGames, setVote, toggleFav, useGameVotes, type GameStat, type Vote } from '@/lib/gameVotes';
import { recordClick, useGamePulse, type GamePulse, type GamePulses, type PulseTotals } from '@/lib/gamePulse';
import { fetchPremiumGames, buyGame, type PremiumGameInfo } from '@/lib/api';
import MetalFrame from './MetalFrame';
import MarqCoin from './MarqCoin';
import TopEarnersWidget from './TopEarnersWidget';
import { GAME_VIDEOS } from '@/lib/gameVideos';
import { ensureVideo, localVideoUrl, useGameEnable, videoReady } from '@/lib/videoCache';
import { openGuestPrompt } from './GuestPrompt';
import { READY_GAMES } from './loaders';

/** Base for API calls, like the other game libs (lib/gamePlays, lib/gameVotes). */
const API = process.env.NEXT_PUBLIC_API_BASE ?? '';

/**
 * This person's Favourites, in order: their three most-played games (never one
 * they voted down), then every game they put there with the ❤.
 * `top` is the most-played part, so a tile can say why it is there.
 */
function useFavourites(shelfGames: Game[], votes: Record<string, Vote>, favs: string[]): { list: string[]; top: string[] } {
  const account = useAuth().user?.id ?? null;
  const plays = useGamePlays(account);
  const ids = shelfGames.map((g) => g.id);
  const top = topPlayed(plays, ids, (id) => votes[id] === 'dislike');
  const hearted = ids
    .filter((id) => favs.includes(id) && !top.includes(id))
    .sort((a, b) => popularityRank(a) - popularityRank(b));
  return { list: [...top, ...hearted], top };
}

/**
 * A game's icon at chip size: its first screenshot, cropped square.
 *
 * A real frame of the game rather than the drawn tile, because at 20px the
 * thing people recognise is the game's own colours. A game without
 * screenshots falls back to its drawn cover.
 */
function GameIcon({ game }: { game: Game }) {
  const [drawn, setDrawn] = useState(false);
  useEffect(() => { setDrawn(false); }, [game.id]);
  return (
    <span className="block h-6 w-6 shrink-0 overflow-hidden rounded-md bg-ink-900 ring-1 ring-black/10 dark:ring-white/20">
      {drawn ? (
        <GameCover game={game} className="h-full w-full" />
      ) : (
        // eslint-disable-next-line @next/next/no-img-element -- a 24px decorative crop
        <img src={`/games/covers/${game.id}-1.webp`} alt="" onError={() => setDrawn(true)}
             className="h-full w-full object-cover" draggable={false} />
      )}
    </span>
  );
}

/**
 * The Games room's top bar: the site's one navbar (components/nav/SiteNav.tsx),
 * with the room's tools in it. On the shelf: search. Inside a game:
 * the game's name (with its cover as the icon), its menu, ❤ and votes, and the
 * ✕ back to the shelf. The Games tab is lit throughout and also leads back to
 * the shelf.
 */
function GamesBar({ game, onClose, closeTitle, children, tone, search = null }: {
  game?: Game;
  /** The bar's centre: the shelf's own search. None inside a game. */
  search?: React.ReactNode;
  /** `dark` on the shelf, whose night sky is always dark. */
  tone?: 'auto' | 'dark';
  /** Absent on the shelf, which is a page and not a layer. */
  onClose?: () => void;
  closeTitle?: string;
  children?: React.ReactNode;
}) {
  return (
    <SiteNav onClose={onClose} closeTitle={closeTitle} sticky={false} tone={tone} search={search}>
      {game && (
        <span title={game.blurb} className="flex h-9 min-w-0 items-center gap-2 rounded-full bg-ink-100 pl-1.5 pr-3.5 text-[13.5px] font-semibold text-ink-900 dark:bg-white/[0.08] dark:text-white">
          <GameIcon game={game} />
          <span className="truncate">{game.name}</span>
        </span>
      )}
      {children}
    </SiteNav>
  );
}

/**
 * Like / dislike for one game, each with everyone's live count (lib/gameVotes.ts).
 * They are the up- and downvotes on the game's post in c/games, so a game
 * exists by its post: `big` (the open game's bar) also links to that post.
 * One vote per person: the lit one takes the vote back, the other moves it.
 */
function VoteButtons({ id, stat, vote, big = false, className = '' }: {
  id: string; stat?: GameStat; vote?: Vote; big?: boolean; className?: string;
}) {
  const post = stat?.post;
  return (
    <span className={'flex shrink-0 items-center gap-1 ' + className}>
      {big && post && (
        <a href={`/c/${post.slug}/p/${post.id}`} onClick={(e) => e.stopPropagation()}
           title={`This game's post in c/${post.slug}: its likes are the post's upvotes · ${post.comments} ${post.comments === 1 ? 'comment' : 'comments'}`}
           className="flex items-center gap-1 rounded-full border border-ink-200 px-2.5 py-1.5 text-[12px] font-semibold tabular-nums text-ink-500 transition hover:border-ink-400 hover:text-ink-800 dark:border-ink-700 dark:text-ink-400 dark:hover:border-ink-500 dark:hover:text-ink-100">
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden><path d="M4 5h16v11H9l-5 4z" /></svg>
          <span>{post.comments}</span><span className="sr-only">comments on this game's post</span>
        </a>
      )}
      {(['like', 'dislike'] as Vote[]).map((kind) => {
        const up = kind === 'like';
        const on = vote === kind;
        const n = (up ? stat?.likes : stat?.dislikes) ?? 0;
        const label = on ? (up ? 'Remove your like' : 'Remove your dislike') : (up ? 'Like this game' : 'Dislike this game');
        return (
          <button
            key={kind}
            type="button"
            onClick={(e) => { e.stopPropagation(); setVote(id, on ? null : kind); }}
            title={`${label} · ${n} ${up ? (n === 1 ? 'like' : 'likes') : (n === 1 ? 'dislike' : 'dislikes')}`}
            aria-label={`${label} (${n})`}
            aria-pressed={on}
            className={
              'flex items-center gap-1 rounded-full border font-semibold tabular-nums transition active:scale-95 ' +
              (big ? 'px-2.5 py-1.5 text-[12px] ' : 'px-2 py-0.5 text-[11px] ') +
              (on
                ? (up
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:border-emerald-400 dark:bg-emerald-500/15 dark:text-emerald-300'
                  : 'border-rose-500 bg-rose-50 text-rose-600 dark:border-rose-400 dark:bg-rose-500/15 dark:text-rose-300')
                : 'border-ink-200 text-ink-500 hover:border-ink-400 hover:text-ink-800 dark:border-ink-700 dark:text-ink-400 dark:hover:border-ink-500 dark:hover:text-ink-100')
            }
          >
            <ThumbIcon down={!up} filled={on} className={big ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
            <span>{n}</span>
          </button>
        );
      })}
    </span>
  );
}

function HeartIcon({ filled, className = 'h-4 w-4' }: { filled?: boolean; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden fill={filled ? 'currentColor' : 'none'}
         stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
      <path d="M12 20.5s-7.4-4.5-9.4-9C1.2 8.2 3.2 4.5 6.7 4.5c2 0 3.5 1.1 5.3 3 1.8-1.9 3.3-3 5.3-3 3.5 0 5.5 3.7 4.1 7-2 4.5-9.4 9-9.4 9z" />
    </svg>
  );
}

/** ❤ — in or out of "Your favourites". Personal: it changes no count. */
function FavButton({ id, on, onCover = false, className = '' }: { id: string; on: boolean; onCover?: boolean; className?: string }) {
  const label = on ? 'Remove from your favourites' : 'Add to your favourites';
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); toggleFav(id); }}
      title={label}
      aria-label={label}
      aria-pressed={on}
      className={
        'grid shrink-0 place-items-center rounded-full transition active:scale-90 ' +
        (onCover
          ? 'h-8 w-8 bg-black/55 shadow-lg ring-1 ring-white/15 backdrop-blur-sm ' + (on ? 'text-rose-500' : 'text-white/90 hover:text-white')
          : 'h-8 w-8 border ' + (on
            ? 'border-rose-400 bg-rose-50 text-rose-500 dark:border-rose-400 dark:bg-rose-500/15'
            : 'border-ink-200 text-ink-400 hover:border-rose-300 hover:text-rose-500 dark:border-ink-700')) +
        ' ' + className
      }
    >
      <HeartIcon filled={on} />
    </button>
  );
}

/**
 * An old invite link. Online-match invites were `/?play=<id>&join=<CODE>`
 * when a game was a dialog over the translator, and those links are still out
 * there — pasted in chats, printed on the lobby's own "copy invite" button in
 * every game. Mounted on the translator page, it forwards them to the game's
 * page with the room code intact. Draws nothing.
 */
export function LegacyInviteRedirect() {
  const router = useRouter();
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const g = GAMES.find((x) => x.id === q.get('play'));
    if (!g) return;
    const keep = new URLSearchParams();
    for (const k of ['join', 'pw']) { const v = q.get(k); if (v) keep.set(k, v); }
    const rest = keep.toString();
    router.replace(`/games/${g.id}${rest ? `?${rest}` : ''}`);
  }, [router]);
  return null;
}

/**
 * The Games room: `/games` is the shelf, `/games/<id>` is one game.
 *
 * Mounted ONCE, by app/games/layout.tsx, and it reads the address to decide
 * what to show — the two pages under that layout render nothing themselves.
 * That is deliberate, and it is what keeps the room feeling like one place:
 *
 *  · THE SHELF STAYS MOUNTED under a game. A layout survives navigation between
 *    its own pages, so search text, scroll position and the chosen density are
 *    exactly where they were when a game closes. Two independent pages would
 *    rebuild the shelf from nothing on every way back.
 *  · THE ADDRESS IS THE STATE. Which game is open is the URL and nothing else,
 *    so Back and Forward step between shelf and game, a reload stays in the
 *    game, and a game can be linked to. There is no `playing` flag to disagree
 *    with the address bar.
 *
 * What a game's page shows depends on whether it can be played yet:
 *
 *   locked (premium, not owned)   its information page — the pitch, the
 *                                 tutorial video, the in-game shop, the top
 *                                 earners, and the price. Buying turns the same
 *                                 page into the game.
 *   playable                      the game, under the bar that names it.
 *
 * The server decides which; until its catalogue has answered, the page waits
 * rather than guess, because guessing "playable" would hand out a paid game.
 */
export function GamesRoom() {
  const router = useRouter();
  const pathname = usePathname() || '/games';
  const id = pathname.startsWith('/games/') ? decodeURIComponent(pathname.slice('/games/'.length).split('/')[0]) : '';
  const game = id ? GAMES.find((g) => g.id === id) ?? null : null;

  // An address for a game that does not exist (or is off the shelf): the shelf.
  useEffect(() => { if (id && !game) router.replace('/games'); }, [id, game, router]);

  /**
   * Premium games: the catalogue (price, reward, pitch) and which this account
   * owns. The server is the authority on both; a failed fetch leaves both empty,
   * which makes every game play as free — the safe way for this to degrade.
   * Re-fetched when the signed-in account changes, so a sign-in reveals the
   * games that account already owns.
   */
  const { user } = useAuth();
  const [premium, setPremium] = useState<Record<string, PremiumGameInfo>>({});
  const [owned, setOwned] = useState<Set<string>>(new Set());
  const [premiumReady, setPremiumReady] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchPremiumGames()
      .then((d) => { if (!alive) return; setPremium(d.games || {}); setOwned(new Set(d.owned || [])); })
      .catch(() => { /* premium off / offline → all games play as free */ })
      .finally(() => { if (alive) setPremiumReady(true); });
    return () => { alive = false; };
  }, [user?.id]);

  /** The lock on a game: its catalogue entry when it is premium AND not owned. */
  const lockOf = (g: Game): PremiumGameInfo | null =>
    premium[g.id] && !owned.has(g.id) ? premium[g.id] : null;

  /**
   * An online-match invite: `/games/<id>?join=<CODE>&pw=<…>`. The room code is
   * handed to the game's page, which joins by itself; the parameters are then
   * taken out of the address so a reload does not join a finished room again.
   * Read once per game, and dropped when the game is left.
   */
  const [invite, setInvite] = useState<{ game: string; code: string; pw: string | null } | null>(null);
  useEffect(() => {
    if (!game) { setInvite(null); return; }
    const q = new URLSearchParams(window.location.search);
    const code = (q.get('join') || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 12);
    if (!code) return;
    // A room password, only when the host chose to put it in the link.
    const pw = (q.get('pw') || '').slice(0, 32);
    setInvite({ game: game.id, code, pw: pw || null });
    q.delete('join'); q.delete('pw');
    const rest = q.toString();
    window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash);
  }, [game]);
  const join = invite && game && invite.game === game.id ? invite : null;

  /**
   * The one funnel every launch off the shelf goes through — tiles, rows and
   * the search Enter. It ends in a navigation; the game's page then decides
   * between the information page and the game.
   */
  const pick = (g: Game) => {
    // No tutorial in this browser yet: the click is "Watch video" — load it
    // (the tile shows the progress, the shelf stays put), then carry on to the
    // game's page with the video ready to play.
    if (!videoReady(g.id)) {
      void ensureVideo(g.id).then((ok) => { if (ok) pickRef.current(g); });
      return;
    }
    recordClick(g.id);   // one open off the shelf — counts guests too (lib/gamePulse)
    router.push(`/games/${g.id}`);
  };
  const pickRef = useRef(pick);
  pickRef.current = pick;

  const toShelf = () => router.push('/games');
  const lock = game && premiumReady ? lockOf(game) : null;

  return (
    <>
      <GamesBrowser
        onPick={pick}
        onClose={() => router.push('/')}
        premium={premium}
        owned={owned}
        covered={!!game}
      />

      {game && !premiumReady && (
        // The catalogue has not answered yet. The bar is already the game's, so
        // the page does not jump when the answer lands.
        <div className="fixed inset-0 z-[100] flex" role="dialog" aria-modal="true" aria-label={game.name} aria-busy="true">
          <div className="flex h-[100dvh] w-full flex-col overflow-hidden bg-white dark:bg-ink-900">
            <GamesBar game={game} onClose={toShelf} closeTitle="Close — back to all games" />
            <div className="grid flex-1 place-items-center text-sm text-ink-500 dark:text-ink-400">Opening {game.name}…</div>
          </div>
        </div>
      )}

      {game && premiumReady && !lock && (
        <GameDialog
          game={game}
          join={join?.code ?? null}
          joinPw={join?.pw ?? null}
          onClose={toShelf}
        />
      )}

      {game && lock && (
        <PurchaseDialog
          game={game}
          info={premium[game.id]}
          onClose={toShelf}
          // Bought → the same address is now the game.
          onOwned={() => setOwned((prev) => { const n = new Set(prev); n.add(game.id); return n; })}
        />
      )}
    </>
  );
}

/* ── The shelf, full size ──────────────────────────────────────────────────
 *
 * This used to be a 19rem dropdown, and forty-one games do not fit in one:
 * three quarters of the shelf was below the fold of a scrolling list, so the
 * back half of it was effectively unreachable. A dialog with covers, a search
 * box and a choice of two densities is the same data made findable.
 *
 * Three decisions worth writing down:
 *
 *  · TILES BY DEFAULT, because a wall of pictures is how anybody actually
 *    picks a game — you recognise one before you read its name. Rows are there
 *    for the person who knows what they want and wants the whole list at once,
 *    and the choice is remembered on the device rather than in the session.
 *  · SEARCH FLATTENS THE GROUPS. Filtering inside three headings leaves you
 *    reading empty sections to find the two that matched; a search is a
 *    different view of the shelf, not the same view with holes in it.
 *  · The search box takes focus on open, so typing works immediately, and
 *    Enter opens the first result — the fastest possible path from "I want
 *    Tetris" to playing it.
 *  · RATED GAMES MOVE SHELF rather than moving within one. A liked game goes
 *    into Favourites at the very top and LEAVES its group; a disliked one goes
 *    to the bottom shelf and leaves it too. Sorting inside the groups instead
 *    would have buried a liked board game under forty arcade games — "top of
 *    the ranking" has to mean the top of the menu, not the top of the fourth
 *    heading — and listing it in both places would put the same cover on
 *    screen twice, which reads as a bug in a grid of pictures.
 */
/**
 * The night sky behind the games shelf.
 *
 * Not colour — a calm night: a deep navy sky, one glowing moon, and a field of
 * stars that drift slowly and twinkle on their own clocks, with the rare
 * shooting star crossing it. The shelf renders in its dark theme (the `dark`
 * scope on the overlay) so the scene is one piece. It is `aria-hidden` and
 * `pointer-events-none`, sits UNDER the content (z-0 vs z-10), and the motion is
 * CSS (globals.css: gt-star-* / gt-moon / gt-shoot), so it costs nothing to run
 * and holds still under prefers-reduced-motion.
 */

/* A tiny seeded RNG so the star field is the SAME sky every time — reviewable,
 * and never a server/client mismatch. Deterministic from one seed. */
function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** ~84 stars: a few bright and large, most faint and small. */
const STARS = (() => {
  const r = mulberry32(20261010);
  return Array.from({ length: 84 }, () => {
    const bright = r() < 0.16;
    return {
      left: +(r() * 100).toFixed(2),
      top: +(r() * 100).toFixed(2),
      size: bright ? +(1.8 + r() * 1.6).toFixed(2) : +(0.7 + r() * 1).toFixed(2),
      o: bright ? +(0.85 + r() * 0.15).toFixed(2) : +(0.3 + r() * 0.45).toFixed(2),
      d: +(2.6 + r() * 4.5).toFixed(2),
      delay: -+(r() * 7).toFixed(2),
      bright,
    };
  });
})();

/** A couple of shooting stars, far apart in time so they stay a surprise. */
const SHOOTERS = [
  { top: '16%', left: '6%', rot: 20, w: 120, len: '620px', d: '11s', delay: '-3s' },
  { top: '30%', left: '46%', rot: 24, w: 90, len: '520px', d: '17s', delay: '-11s' },
];

function ShelfBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      {/* The deep night, with the faintest glow up where the moon hangs. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(130% 95% at 84% 8%, #1c2850 0%, #111b3c 32%, #0b1430 55%, #070d22 76%, #04081a 100%)',
        }}
      />

      {/* The moon — a pale disc with a soft halo and one shaded limb. */}
      <div
        className="gt-moon absolute rounded-full"
        style={{
          top: '13%', right: '8%', width: 92, height: 92,
          background: 'radial-gradient(circle at 36% 34%, #f8fafc 0%, #e4e9f1 46%, #c3ccdb 72%, #9faabf 100%)',
          boxShadow:
            '0 0 60px 16px rgba(203,213,225,0.20), 0 0 130px 44px rgba(148,163,184,0.12), inset -13px -11px 28px rgba(51,65,85,0.45)',
        }}
      >
        <span className="absolute rounded-full" style={{ left: '30%', top: '30%', width: 13, height: 13, background: 'rgba(71,85,105,0.22)' }} />
        <span className="absolute rounded-full" style={{ left: '56%', top: '52%', width: 9, height: 9, background: 'rgba(71,85,105,0.18)' }} />
        <span className="absolute rounded-full" style={{ left: '44%', top: '20%', width: 6, height: 6, background: 'rgba(71,85,105,0.16)' }} />
      </div>

      {/* The star field — drifting slowly as one, each star on its own twinkle. */}
      <div className="gt-star-field absolute inset-[-6%]">
        {STARS.map((s, i) => (
          <span
            key={i}
            className="gt-star absolute rounded-full bg-white"
            style={{
              left: `${s.left}%`, top: `${s.top}%`, width: s.size, height: s.size,
              boxShadow: s.bright ? '0 0 6px 1px rgba(255,255,255,0.75)' : undefined,
              '--o': s.o, '--d': `${s.d}s`, '--delay': `${s.delay}s`,
            } as React.CSSProperties}
          />
        ))}
      </div>

      {/* The shooting stars: a bright head with a tail that fades behind it. */}
      {SHOOTERS.map((s, i) => (
        <span key={i} className="absolute" style={{ top: s.top, left: s.left, transform: `rotate(${s.rot}deg)` }}>
          <span
            className="gt-shoot block h-px rounded-full"
            style={{
              width: s.w,
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.95))',
              '--d': s.d, '--delay': s.delay, '--len': s.len,
            } as React.CSSProperties}
          />
        </span>
      ))}
    </div>
  );
}

function GamesBrowser({
  onPick,
  onClose,
  premium,
  owned,
  covered,
}: {
  onPick: (g: Game) => void;
  onClose: () => void;
  premium: Record<string, PremiumGameInfo>;
  owned: Set<string>;
  /** A game or a purchase is open on top of this. The shelf waits underneath. */
  covered: boolean;
}) {
  /** The lock on a game — its price entry when premium and not yet owned. */
  const lockOf = (g: Game): PremiumGameInfo | null =>
    premium[g.id] && !owned.has(g.id) ? premium[g.id] : null;
  const [q, setQ] = useState('');
  // One view, tiles: the owner had the Tiles/Rows switch taken off the bar (2026-10-14).
  const view: 'tiles' | 'rows' = 'tiles';
  const search = useRef<HTMLInputElement>(null);
  const me = useAuth().user;
  // Console catalogue overrides (visibility, name, blurb, shelf, colour, NEW,
  // access, order). Fetched once per open; the server caches them, and an empty
  // map means the pure code registry — so a failed fetch degrades to defaults.
  const [overrides, setOverrides] = useState<GameOverrides>({});
  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/games/overrides`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d?.overrides) setOverrides(d.overrides as GameOverrides); })
      .catch(() => { /* keep code defaults */ });
    return () => { alive = false; };
  }, []);
  const shelfGames = gamesForWith(me?.nickname, overrides);
  const { stats, votes, favs } = useGameVotes(me?.id ?? null);
  const pulse = useGamePulse();
  const { list: favourites, top } = useFavourites(shelfGames, votes, favs);
  /*
   * The play totals the shelf is ORDERED by: a copy of the pulse taken when
   * the shelf comes up (and again each time a game closes back onto it), not
   * the live one. The numbers on the cards tick every few seconds; cards
   * swapping places under the pointer every few seconds would be a shelf
   * nobody can click.
   */
  const [played, setPlayed] = useState<GamePulses>(pulse.games);
  const pulsed = Object.keys(pulse.games).length > 0;
  useEffect(() => {
    if (!covered && pulsed) setPlayed(pulse.games);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a snapshot on purpose, see above
  }, [covered, pulsed]);

  useEffect(() => { search.current?.focus(); }, []);

  useEffect(() => {
    // Escape closes the TOP level. While a game is up that is not this one.
    if (covered) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, covered]);

  const needle = q.trim().toLowerCase();
  const hit = (g: Game) =>
    !needle ||
    g.name.toLowerCase().includes(needle) ||
    g.blurb.toLowerCase().includes(needle) ||
    GROUP_LABEL[g.group].toLowerCase().includes(needle);
  /*
   * Search results are ranked but not sectioned: two matches out of forty-one
   * do not need three headings over them, and a favourite that matched should
   * still be the one Enter opens.
   */
  const found = rankGames(shelfGames.filter(hit), favourites, played);

  // Favourites (most-played three, then own likes), then every other game by
  // everyone's play totals, most played first — the least played come last.
  const ranked = rankGames(shelfGames, favourites, played);
  const liked = ranked.filter((g) => favourites.includes(g.id));
  /*
   * The chart: every played game's place among ALL the games (#1 = most
   * played), whichever section it is drawn in — a favourite keeps its true
   * place, so "Your favourites" can read #7, #1, #19. `share` is how it
   * measures against #1, for the meter; square-rooted, because one runaway hit
   * would otherwise flatten every other bar to a sliver.
   */
  const charted = rankGames(shelfGames, [], played).filter((g) => (played[g.id]?.plays ?? 0) > 0);
  const topPlays = played[charted[0]?.id]?.plays ?? 0;
  const chart: Record<string, Standing> = {};
  charted.forEach((g, i) => { chart[g.id] = { rank: i + 1, plays: played[g.id].plays, share: Math.sqrt(played[g.id].plays / topPlays) }; });
  const rest = ranked.filter((g) => !favourites.includes(g.id));

  const sections: { key: string; title: string; games: Game[] }[] = needle
    ? [{ key: 'found', title: `${found.length} result${found.length === 1 ? '' : 's'}`, games: found }]
    : [
        ...(liked.length ? [{ key: 'liked', title: 'Your favourites', games: liked }] : []),
        { key: 'all', title: liked.length ? 'More games · most played first' : 'All games · most played first', games: rest },
      ];

  return (
    // The whole window, edge to edge, at every size (owner, 2026-10-13). It used
    // to float with a margin of backdrop round it; that margin bought a dimmed
    // strip of translator and cost the shelf a row of games.
    //
    // `dark`: the shelf is a night sky, and a night sky only works dark — so the
    // whole dialog renders in its dark theme regardless of the app's, which
    // makes the tiles, text and borders one piece with the stars behind them.
    //
    // `covered`: a level is open above. The shelf stays MOUNTED — that is what
    // keeps its scroll position and search for the way back — but it is taken
    // out of sight, out of the tab order and away from the pointer, so nothing
    // under a running game is painting stars or catching a key.
    <div
      className={'fixed inset-0 z-50 flex' + (covered ? ' invisible pointer-events-none' : '')}
      aria-hidden={covered || undefined}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Games"
        className="relative z-10 flex h-[100dvh] w-full max-w-none flex-col overflow-hidden bg-ink-900"
      >
        <ShelfBackdrop />

        <GamesBar tone="dark" search={
          <div className="relative w-full min-w-[120px]">
            <svg viewBox="0 0 24 24" aria-hidden
                 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400 dark:text-white/40"
                 fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" />
            </svg>
            <input
              ref={search}
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && found.length) onPick(found[0]);
              }}
              placeholder="Search games"
              aria-label="Search games"
              className={NAV_SEARCH}
            />
          </div>
        } />

        <div className="dark relative z-10 min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {!needle && <ShelfHero totals={pulse.totals} />}

          {!found.length && (
            <p className="py-16 text-center text-[13px] text-ink-500 dark:text-ink-400">
              Nothing here called “{q.trim()}”.
            </p>
          )}

          {sections.map((sec) =>
            sec.games.length ? (
              <div key={sec.key} className="mb-5 last:mb-1">
                <p className="px-0.5 pb-2 text-[10px] font-semibold uppercase tracking-wider text-white/45">
                  {sec.title}
                </p>
                {view === 'tiles' ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                    {sec.games.map((g) => (
                      <Tile key={g.id} game={g} onPick={onPick} standing={chart[g.id]} stat={stats[g.id]} pulse={pulse.games[g.id]} vote={votes[g.id]} fav={favs.includes(g.id)} auto={top.includes(g.id) && !favs.includes(g.id)} lock={lockOf(g)} />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-0.5">
                    {sec.games.map((g) => (
                      <Row key={g.id} game={g} onPick={onPick} standing={chart[g.id]} showGroup={!!needle} stat={stats[g.id]} pulse={pulse.games[g.id]} vote={votes[g.id]} fav={favs.includes(g.id)} lock={lockOf(g)} />
                    ))}
                  </div>
                )}
              </div>
            ) : null
          )}
        </div>
      </div>
    </div>
  );
}


/** The two-line clamp, written out because it is a vendor property. */
const CLAMP2 = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
};

function NewBadge({ className = '' }: { className?: string }) {
  return (
    <span className={
      'shrink-0 rounded-full bg-accent-600 px-1.5 py-px text-[9px] font-bold uppercase ' +
      'tracking-wide text-white dark:bg-accent-400 dark:text-ink-900 ' + className
    }>
      New
    </span>
  );
}

/*
 * A thumb, drawn once.
 *
 * `rotate(180)` rather than a second path: a thumbs-down IS a thumbs-up upside
 * down, and two hand-written hands would be two hands to keep in agreement
 * every time the outline is nudged. Two subpaths so it survives being drawn
 * unfilled — the seam between the hand and the pad is what makes it read as a
 * fist rather than a blob.
 */
const THUMB_HAND = 'M9.4 20V9.7l3.4-6.4a1.7 1.7 0 013.2.8V9h3.6a1.9 1.9 0 011.85 2.35l-1.5 6.15A2 2 0 0118 20z';
const THUMB_PAD = 'M9.4 20H6.4A1.4 1.4 0 015 18.6v-7.5A1.4 1.4 0 016.4 9.7h3z';

function ThumbIcon({ down, filled, className = 'h-4 w-4' }: {
  down?: boolean; filled?: boolean; className?: string;
}) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={className}
         fill={filled ? 'currentColor' : 'none'} stroke="currentColor"
         strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round">
      <g transform={down ? 'rotate(180 12 12)' : undefined}>
        <path d={THUMB_HAND} />
        <path d={THUMB_PAD} />
      </g>
    </svg>
  );
}

/* ── The living numbers ──────────────────────────────────────────────────── */

/** 1234 → "1.2K", 2_600_000 → "2.6M". Compact, because a tile has no room. */
const COMPACT = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });
const fmt = (n: number) => COMPACT.format(Math.max(0, Math.round(n || 0)));

/** A little sparkle — the "AI" mark on the hero. */
function SparkIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8z" />
      <path d="M19 3l.7 2.3L22 6l-2.3.7L19 9l-.7-2.3L16 6l2.3-.7z" opacity=".7" />
    </svg>
  );
}

/** A green beacon that pings — "someone is in this right now". */
function PulseDot({ className = 'h-1.5 w-1.5' }: { className?: string }) {
  return (
    <span className={'relative inline-flex ' + className} aria-hidden>
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
      <span className="relative inline-flex h-full w-full rounded-full bg-emerald-400" />
    </span>
  );
}

/**
 * The three numbers under every card the owner asked for (2026-10-11): how
 * many times it has been opened, how many plays it has, and — a live green
 * pip — how many are in it right now. The player count rides along in the
 * tooltip so the strip stays to one line on a narrow tile.
 */
function StatStrip({ pulse, className = '' }: { pulse?: GamePulse; className?: string }) {
  const clicks = pulse?.clicks ?? 0;
  const plays = pulse?.plays ?? 0;
  const players = pulse?.players ?? 0;
  const live = pulse?.live ?? 0;
  return (
    <div
      className={'flex items-center gap-x-3 gap-y-1 text-[10.5px] font-medium tabular-nums text-white/50 ' + className}
      title={
        `${clicks.toLocaleString()} opened · ${plays.toLocaleString()} plays · ` +
        `${players.toLocaleString()} players${live ? ` · ${live.toLocaleString()} playing now` : ''}`
      }
    >
      <span className="inline-flex items-center gap-1">
        <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" />
          <circle cx="12" cy="12" r="2.6" />
        </svg>
        {fmt(clicks)}
      </span>
      <span className="inline-flex items-center gap-1">
        <svg viewBox="0 0 24 24" className="h-3 w-3" fill="currentColor" aria-hidden>
          <path d="M8 5.5v13l11-6.5z" />
        </svg>
        {fmt(plays)}
      </span>
      {live > 0 && (
        <span className="inline-flex items-center gap-1 text-emerald-400">
          <PulseDot /> {fmt(live)}<span className="hidden text-emerald-400/70 sm:inline">&nbsp;now</span>
        </span>
      )}
    </div>
  );
}

/* ── The chart ───────────────────────────────────────────────────────────── */

/** A game's place among all the games by plays, and how it measures against #1 (0–1). */
interface Standing { rank: number; plays: number; share: number }

/** Gold, silver, bronze — the podium. Everything below it is plain. */
const PODIUM = [
  { chip: 'bg-gradient-to-b from-amber-200 to-amber-500 text-amber-950 ring-amber-100/70', text: 'text-amber-300', bar: 'from-amber-500 to-amber-200' },
  { chip: 'bg-gradient-to-b from-slate-100 to-slate-400 text-slate-900 ring-white/70', text: 'text-slate-200', bar: 'from-slate-400 to-slate-100' },
  { chip: 'bg-gradient-to-b from-orange-300 to-orange-600 text-orange-950 ring-orange-200/60', text: 'text-orange-400', bar: 'from-orange-600 to-orange-300' },
] as const;

const standingTitle = (s: Standing) =>
  `#${s.rank} most played · ${s.plays.toLocaleString()} play${s.plays === 1 ? '' : 's'}`;

function CrownIcon({ className = 'h-3 w-3' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M3 8l4.5 4L12 5l4.5 7L21 8l-1.8 10H4.8z" />
    </svg>
  );
}

/** The place, as a chip on a tile's cover: a medal for the podium, smoked glass below it. */
function RankChip({ standing, className = '' }: { standing: Standing; className?: string }) {
  const medal = PODIUM[standing.rank - 1];
  return (
    <span
      title={standingTitle(standing)}
      className={
        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-px font-bold tabular-nums shadow ring-1 ' +
        (medal ? `text-[11px] ${medal.chip} ` : 'bg-black/60 text-[10px] text-white/85 ring-white/15 backdrop-blur-sm ') +
        className
      }
    >
      {standing.rank === 1 && <CrownIcon />}
      <span aria-hidden>#{standing.rank}</span>
      <span className="sr-only">{standingTitle(standing)}</span>
    </span>
  );
}

/** How a game measures against the most played one — the same bar on every card, so they compare at a glance. */
function PlayMeter({ standing, className = '' }: { standing?: Standing; className?: string }) {
  const medal = standing ? PODIUM[standing.rank - 1] : undefined;
  return (
    <span className={'block overflow-hidden bg-white/10 ' + className} title={standing ? standingTitle(standing) : undefined} aria-hidden>
      <span
        className={'block h-full rounded-r-full bg-gradient-to-r transition-[width] duration-700 ' + (medal ? medal.bar : 'from-accent-600 to-accent-400')}
        style={{ width: `${Math.round((standing?.share ?? 0) * 100)}%` }}
      />
    </span>
  );
}

/** One figure in the hero's strip: the number bright, what it counts quiet. */
function HeroFigure({ label, value, live, coin }: { label: string; value: number; live?: boolean; coin?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      {coin && <MarqCoin className="h-3.5 w-3.5" />}
      {live && value > 0 && <PulseDot />}
      <span className="font-semibold text-white">{fmt(value)}</span>
      {label}
    </span>
  );
}

/** A compact invitation: visible message, optional story, real activity totals. */
function ShelfHero({ totals }: { totals: PulseTotals }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="gt-ideas mb-5" aria-labelledby="shelf-heading">
      <div className="relative z-10 flex items-center justify-between gap-4 px-5 py-5 sm:px-7">
        <div className="min-w-0 max-w-2xl">
          <p className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-teal-200">
            <SparkIcon /> TrueMarch World <span className="text-white/35">/</span>
            <span className="text-white/60">Room for your next idea</span>
          </p>
          <h1 id="shelf-heading" className="text-[28px] font-semibold leading-[1.15] tracking-tight text-white sm:text-4xl">
            Play a little. <span className="gt-ideas-title">Think bigger.</span>
          </h1>
          <p className="mt-2 max-w-lg text-[13px] leading-relaxed text-slate-300">
            Let AI handle the busywork. Give your curiosity some space.
            Play sparks ideas. AI helps you bring them to life.
          </p>
          <button type="button" onClick={() => setOpen((o) => !o)}
            aria-expanded={open} aria-controls="shelf-hero-pitch"
            className="mt-3 inline-flex items-center gap-2 rounded-lg py-1 text-[12px] font-semibold text-teal-200 transition hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-200">
            {open ? 'A little less' : 'The idea behind the play'}
            <span aria-hidden className={'inline-block transition-transform ' + (open ? 'rotate-90' : '')}>↗</span>
          </button>
        </div>
        <div className="gt-idea-scene hidden shrink-0 sm:block" aria-hidden="true">
          <div className="gt-idea-orbit" />
          <div className="gt-idea-core"><SparkIcon className="h-9 w-9" /></div>
          <span className="gt-idea-note gt-idea-note-one">what if?</span>
          <span className="gt-idea-note gt-idea-note-two">play → idea</span>
          <span className="gt-idea-star">✦</span>
        </div>
      </div>
      <div id="shelf-hero-pitch" hidden={!open} className="relative z-10 border-t border-white/10 px-5 py-4 sm:px-7">
        <p className="max-w-2xl text-[13px] leading-relaxed text-slate-300">
          <span className="font-semibold text-white">A playful moment can become a serious idea.</span>{' '}
          Walt Disney imagined Disneyland while watching his daughters ride a merry-go-round.
          A day of play sparked a question: what if families could have fun together?
        </p>
        <p className="mt-3 max-w-2xl text-[13px] leading-relaxed text-slate-300">
          Bring that curiosity here. Try a move. Rethink a strategy. Let your mind wander.
          When a round ends, take the idea that stayed with you and use AI to help make it real.
          Then come back with a fresh perspective. There’s always another move to discover.
        </p>
        <p className="mt-3 text-[14px] font-semibold text-teal-200">Play. Imagine. Build. Repeat.</p>
        <p className="mt-1 max-w-2xl text-[12px] leading-relaxed text-slate-300">
          Life and business both reward a curious mind. Make your next move count.
        </p>
        <a href="https://disneyparksblog.com/disney-experiences/today-in-disney-history-walt-disney-was-born-in-1901/"
          target="_blank" rel="noopener noreferrer"
          className="mt-3 inline-block rounded text-[10px] text-slate-400 underline decoration-white/20 underline-offset-2 hover:text-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-200">
          The Disneyland story · Disney Parks
        </a>
      </div>
      <div className="relative z-10 flex flex-wrap items-center justify-between gap-x-5 gap-y-2 border-t border-white/10 bg-black/10 px-5 py-2.5 sm:px-7">
        <p className="text-[11px] font-medium text-slate-300">Life is a game of possibilities. Make your next move.</p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] tabular-nums text-slate-400">
          <HeroFigure label="plays" value={totals.plays} />
          <HeroFigure label="players" value={totals.players} />
          {totals.live > 0 && <HeroFigure label="playing now" value={totals.live} live />}
          <HeroFigure label="KHARMA earned" value={totals.marqPaid} coin />
        </div>
      </div>
    </section>
  );
}

function Tile({ game, onPick, standing, stat, pulse, vote, fav, auto, lock }: {
  game: Game; onPick: (g: Game) => void; standing?: Standing; stat?: GameStat; pulse?: GamePulse; vote?: Vote; fav: boolean;
  /** In Favourites because it is one of this person's three most played. */
  auto?: boolean;
  /** Set when this game is premium and NOT yet owned — draw it locked. */
  lock?: PremiumGameInfo | null;
}) {
  const [live, setLive] = useState(false);
  const locked = !!lock;
  // No tutorial in this browser yet: the cover carries "Watch video" instead of the hover pill.
  const en = useGameEnable(game.id);
  const gated = !!GAME_VIDEOS[game.id] && en.s !== 'on';
  return (
    <div
      className="group relative flex flex-col overflow-hidden rounded-md bg-white pt-[20px] text-left shadow-sm
                 transition hover:-translate-y-0.5 hover:shadow-xl
                 focus-within:ring-2 focus-within:ring-accent-500/40
                 dark:bg-ink-800/40 dark:shadow-black/40"
      onMouseEnter={() => setLive(true)}
      onMouseLeave={() => setLive(false)}
      onFocus={() => setLive(true)}
      onBlur={() => setLive(false)}
    >
      <button
        type="button"
        onClick={() => onPick(game)}
        aria-label={locked ? `Buy ${game.name} — ${lock!.price.toLocaleString()} KHARMA` : `Play ${game.name}`}
        className="flex flex-1 flex-col text-left focus:outline-none"
      >
        <span className="relative block aspect-[16/10] w-full overflow-hidden bg-ink-900">
          {/*
            A locked game is greyed until you hover it — the shelf then previews,
            in colour, exactly what you would be buying. The filter sits on this
            wrapper so it dims the animated cover without touching the badges.
          */}
          <span
            className={
              'block h-full w-full transition duration-500 ' +
              (locked ? 'grayscale brightness-[.72] group-hover:grayscale-0 group-hover:brightness-100' : '')
            }
          >
            <GameShot game={game} live={live} className="h-full w-full" />
          </span>
          <span className="absolute left-2 top-2 flex items-center gap-1">
            {standing && <RankChip standing={standing} />}
            {isNew(game) && <NewBadge className="shadow" />}
          </span>
          <PlayMeter standing={standing} className="absolute inset-x-0 bottom-0 h-[3px]" />
          {auto && !locked && (
            <span className="absolute bottom-2 left-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white ring-1 ring-white/15 backdrop-blur-sm">
              You play most
            </span>
          )}
          {/* The price, always visible on a locked tile — the one fact that
              decides whether the hover is worth it. */}
          {locked && (
            <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/70 py-0.5 pl-1 pr-2.5 text-[12.5px] font-bold text-white ring-1 ring-white/15 backdrop-blur-sm">
              <MarqCoin className="h-5 w-5" />
              {lock!.price.toLocaleString()}
            </span>
          )}
          {/* On hover: a Purchase pill for a locked game, the play mark otherwise. */}
          <span className={'pointer-events-none absolute inset-0 grid place-items-center opacity-0 transition-opacity duration-200 ' +
                           (gated ? 'hidden' : 'group-hover:opacity-100')}>
            {locked ? (
              <span className="inline-flex scale-90 items-center gap-1.5 rounded-full bg-accent-600 px-3.5 py-1.5
                               text-[12px] font-bold text-white shadow-xl transition-transform duration-300
                               group-hover:scale-100">
                <LockGlyph className="h-3.5 w-3.5" />
                Purchase
              </span>
            ) : (
              <span className="grid h-11 w-11 scale-75 place-items-center rounded-full bg-white/90 text-ink-900 shadow-xl
                               transition-transform duration-300 group-hover:scale-100">
                <svg viewBox="0 0 24 24" className="ml-0.5 h-5 w-5" fill="currentColor">
                  <path d="M8 5.5v13l11-6.5z" />
                </svg>
              </span>
            )}
          </span>
        </span>
        <span className="flex-1 px-3 py-2">
          <span className="flex items-center gap-1.5">
            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink-900 dark:text-ink-dim">
              {game.name}
            </span>
            {locked && (
              <span className="shrink-0 rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-amber-700 ring-1 ring-amber-500/40 dark:text-amber-300">
                Premium
              </span>
            )}
          </span>
          <span className="mt-0.5 block text-[11px] leading-4 text-ink-500 dark:text-ink-400" style={CLAMP2}>
            {game.blurb}
          </span>
        </span>
      </button>
      {/* One bottom row: seen and played on the left; the votes, then the coin
          on games that pay KHARMA, on the right. */}
      <span className="mx-3 flex flex-wrap items-center justify-end gap-x-2 gap-y-1.5 border-t border-white/5 pb-2.5 pt-2">
        <StatStrip pulse={pulse} className="mr-auto shrink-0 !gap-x-2" />
        {/* Kept together: on a card too narrow for one row they drop below as a pair. */}
        <span className="flex shrink-0 items-center gap-1.5">
          <VoteButtons id={game.id} stat={stat} vote={vote} />
          {PAYS_KHARMA.has(game.id) && <PaysBadge />}
        </span>
      </span>
      <FavButton id={game.id} on={fav} onCover className="absolute right-2 top-[28px]" />
      <MetalFrame game={game} />
    </div>
  );
}

/** The KHARMA coin on a game that pays for play (PAYS_KHARMA in lib/games.ts). */
function PaysBadge() {
  return (
    <span
      title="Earn KHARMA: this game pays KHARMA for every mission you clear"
      aria-label="Earns KHARMA"
      role="img"
      className="inline-flex shrink-0"
    >
      <MarqCoin className="h-6 w-6 drop-shadow" />
    </span>
  );
}

/** A small padlock — the one mark that reads as "locked / buy to open". */
function LockGlyph({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor"
         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 018 0v3" />
    </svg>
  );
}

function Row({
  game, onPick, standing, showGroup, stat, pulse, vote, fav, lock,
}: {
  game: Game; onPick: (g: Game) => void; standing?: Standing; showGroup?: boolean; stat?: GameStat; pulse?: GamePulse; vote?: Vote; fav: boolean;
  /** Set when premium and not yet owned. */
  lock?: PremiumGameInfo | null;
}) {
  const [live, setLive] = useState(false);
  const locked = !!lock;
  return (
    <div
      className="group flex w-full items-center gap-2 rounded-xl border border-transparent pr-2
                 transition hover:border-ink-200 hover:bg-ink-100/70 focus-within:border-accent-500
                 dark:hover:border-ink-700 dark:hover:bg-ink-800/60"
      onMouseEnter={() => setLive(true)}
      onMouseLeave={() => setLive(false)}
    >
      <button
        type="button"
        onClick={() => onPick(game)}
        aria-label={locked ? `Buy ${game.name} — ${lock!.price.toLocaleString()} KHARMA` : undefined}
        className="flex min-w-0 flex-1 items-center gap-3 px-2 py-1.5 text-left focus:outline-none"
      >
        {/* The place, down the left edge like a chart — the podium in its metals. */}
        <span
          title={standing ? standingTitle(standing) : undefined}
          className={'flex w-7 shrink-0 flex-col items-center font-bold tabular-nums leading-none ' +
            (standing ? (PODIUM[standing.rank - 1]?.text ?? 'text-white/40') + (standing.rank <= 3 ? ' text-[17px]' : ' text-[13px]') : 'text-[13px] text-white/20')}
        >
          {standing?.rank === 1 && <CrownIcon className="mb-0.5 h-3 w-3" />}
          {standing ? standing.rank : '–'}
        </span>
        <span className="relative block h-12 w-20 shrink-0 overflow-hidden rounded-lg bg-ink-900">
          <span className={'block h-full w-full transition duration-500 ' +
            (locked ? 'grayscale brightness-[.72] group-hover:grayscale-0 group-hover:brightness-100' : '')}>
            <GameShot game={game} live={live} className="h-full w-full" />
          </span>
          {locked && (
            <span className="absolute inset-0 grid place-items-center">
              <LockGlyph className="h-4 w-4 text-white/90 drop-shadow transition-opacity group-hover:opacity-0" />
            </span>
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-[13px] font-medium text-ink-900 dark:text-ink-dim">{game.name}</span>
            {isNew(game) && <NewBadge />}
            {locked && (
              <span className="shrink-0 rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-amber-700 ring-1 ring-amber-500/40 dark:text-amber-300">
                Premium
              </span>
            )}
          </span>
          <span className="block truncate text-[11px] text-ink-500 dark:text-ink-400">{game.blurb}</span>
        </span>
        {/* Which shelf it came from — worth saying in a flat list of search
            results, and pure repetition under a heading that already says it. */}
        {showGroup && (
          <span className="hidden shrink-0 text-[10px] uppercase tracking-wider text-ink-400 sm:block">
            {GROUP_LABEL[game.group]}
          </span>
        )}
      </button>
      <PlayMeter standing={standing} className="hidden h-1.5 w-20 shrink-0 rounded-full lg:block" />
      <StatStrip pulse={pulse} className="hidden shrink-0 md:flex" />
      <VoteButtons id={game.id} stat={stat} vote={vote} />
      <span className="flex w-6 shrink-0 justify-center">{PAYS_KHARMA.has(game.id) && <PaysBadge />}</span>
      <FavButton id={game.id} on={fav} />
      {locked ? (
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent-600 py-1 pl-1.5 pr-3 text-[12.5px] font-bold text-white">
          <MarqCoin className="h-5 w-5" />
          {lock!.price.toLocaleString()}
        </span>
      ) : (
        <svg viewBox="0 0 24 24" aria-hidden
             className="h-4 w-4 shrink-0 text-ink-300 transition group-hover:text-accent-600 dark:text-ink-600 dark:group-hover:text-accent-400"
             fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M9 5l7 7-7 7" />
        </svg>
      )}
    </div>
  );
}

/**
 * The storefront for one premium game — what opens when a locked game is picked.
 *
 * It has to do the job a grey tile cannot: show the game MOVING (the same
 * animated cover the shelf uses, forced to play, enlarged), say what it is, name
 * the price in KHARMA and what you can earn back, and take the payment. The price
 * is the server's — this only displays it — and the purchase is one idempotent
 * call; on success the account owns the game for good and it launches straight
 * away. A guest is sent to sign-up instead, because KHARMA lives in an account.
 */
function PurchaseDialog({
  game,
  info,
  onClose,
  onOwned,
}: {
  game: Game;
  info: PremiumGameInfo | undefined;
  /** Step back down to the shelf. */
  onClose: () => void;
  onOwned: () => void;
}) {
  const { user, marq } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One purchase id for this open dialog — reused on a retry so a double-tap or
  // a lost response never charges twice (the server keys idempotency on it).
  const nonce = useRef('g-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8));
  const items = useGameShopItems(game.id);
  const video = GAME_VIDEOS[game.id];
  // The left panel's tabs (owner 2026-10-12): Overview · In-game shop · Top earners.
  const [tab, setTab] = useState<'overview' | 'shop' | 'earners'>('overview');
  const buyTabs: { id: 'overview' | 'shop' | 'earners'; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    ...(items.length ? [{ id: 'shop' as const, label: 'In-game shop', count: items.length }] : []),
    { id: 'earners', label: 'Top earners' },
  ];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const price = info?.price ?? 0;
  const balance = marq?.balance ?? null;
  const short = balance !== null && balance < price;

  const buy = async () => {
    if (!user) { openGuestPrompt('buy'); onClose(); return; }
    setBusy(true);
    setError(null);
    try {
      await buyGame(game.id, nonce.current);
      onOwned();
    } catch (e) {
      setError((e as Error).message || 'Purchase failed — nothing was charged.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex">
      {/*
        The whole window, like the shelf it opened over and the game it leads to:
        the description, what is inside and the price on the left; the tutorial
        video — the game being played and won — on the right.
      */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Buy ${game.name}`}
        className="relative z-10 flex h-[100dvh] w-full flex-col overflow-hidden bg-white dark:bg-ink-900"
      >
        <GamesBar game={game} onClose={onClose} closeTitle="Close — back to all games">
          <span className="flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-amber-400/50 bg-amber-50 px-3 text-[12px] font-semibold text-amber-800 dark:border-amber-300/30 dark:bg-amber-400/10 dark:text-amber-200">
            <LockGlyph className="h-3.5 w-3.5" /> Premium
          </span>
        </GamesBar>

        <div className="grid min-h-0 flex-1 grid-cols-1 content-start overflow-y-auto lg:content-stretch lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:overflow-hidden">
          {/* RIGHT on desktop (first on a phone): the tutorial video, big. */}
          <section className="order-1 flex min-h-0 flex-col justify-center gap-3 bg-ink-950 p-3 sm:p-5 lg:order-2">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-orange-400">
                Tutorial <span className="text-white/40">·</span> <span className="text-white">how to win</span>
              </p>
              {video && <p className="text-[11px] tabular-nums text-white/50">{fmtSecs(video.duration)}</p>}
            </div>
            <TutorialPlayer game={game} />
          </section>

          {/* LEFT on desktop (owner 2026-10-12 redesign): who it is, three tabs of detail, and the buy box pinned below. */}
          <section className="order-2 flex min-h-0 flex-col lg:order-1">
            <div className="px-5 pt-5 sm:px-7 sm:pt-6">
              <p className="text-xs font-medium text-ink-500 dark:text-ink-400">{GROUP_LABEL[game.group]} · Premium game</p>
              <h2 className="mt-1 text-3xl font-semibold tracking-tight text-ink-900 dark:text-white">{game.name}</h2>
              <p className="mt-2 text-sm leading-relaxed text-ink-600 dark:text-ink-300">{info?.pitch || game.blurb}</p>

              <div role="tablist" aria-label={`${game.name} details`} className="mt-5 flex gap-5 border-b border-ink-200 dark:border-white/10">
                {buyTabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.id}
                    onClick={() => setTab(t.id)}
                    className={
                      '-mb-px border-b-2 pb-2.5 text-sm font-medium transition focus:outline-none focus-visible:text-ink-900 dark:focus-visible:text-white ' +
                      (tab === t.id
                        ? 'border-ink-900 text-ink-900 dark:border-white dark:text-white'
                        : 'border-transparent text-ink-500 hover:text-ink-900 dark:text-ink-400 dark:hover:text-white')
                    }
                  >
                    {t.label}
                    {t.count != null && <span className="ml-1.5 text-xs text-ink-400 dark:text-ink-500">{t.count}</span>}
                  </button>
                ))}
              </div>
            </div>

            <div role="tabpanel" className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-7">
              {tab === 'overview' && (
                <div className="grid gap-5">
                  <ul className="divide-y divide-ink-200 rounded-xl border border-ink-200 dark:divide-white/10 dark:border-white/10">
                    {[
                      info?.missions ? [`${info.missions} missions`, 'Each ends with a boss fight'] : null,
                      ['Every mission pays KHARMA', 'Clear it again to earn more'],
                      ['A full in-game shop', 'Weapons, armour and a partner bot'],
                      ['Yours for good', 'Buy once, play any time'],
                    ].filter(Boolean).map((row) => {
                      const [a, b] = row as string[];
                      return (
                        <li key={a} className="flex items-start gap-3 px-4 py-3">
                          <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0 text-ink-900 dark:text-white" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12l5 5 9-10" /></svg>
                          <span>
                            <span className="block text-sm font-medium text-ink-900 dark:text-white">{a}</span>
                            <span className="block text-xs text-ink-500 dark:text-ink-400">{b}</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {info && info.reward > 0 && (
                    <div className="flex items-center gap-3 rounded-xl border border-ink-200 px-4 py-3 dark:border-white/10">
                      <MarqCoin className="h-8 w-8 shrink-0" />
                      <p className="text-sm text-ink-600 dark:text-ink-300">
                        <span className="font-semibold text-ink-900 dark:text-white">Earn up to {info.reward.toLocaleString()} KHARMA back</span>
                        <span className="block text-xs text-ink-500 dark:text-ink-400">by clearing all {info.missions} missions</span>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {tab === 'shop' && (
                <div>
                  <p className="mb-3 text-xs text-ink-500 dark:text-ink-400">Bought with KHARMA during a run. Prices are per pack.</p>
                  <ul className="divide-y divide-ink-200 rounded-xl border border-ink-200 dark:divide-white/10 dark:border-white/10">
                    {items.map((it) => (
                      <li key={it.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                        <span className="truncate text-ink-800 dark:text-ink-dim">{it.name}</span>
                        <span className="inline-flex shrink-0 items-center gap-1.5 font-medium tabular-nums text-ink-900 dark:text-white">
                          <MarqCoin className="h-4 w-4" />{it.price.toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {tab === 'earners' && <TopEarnersWidget game={game.id} />}
            </div>

            {/* The buy box — always in view, whatever the tab. */}
            <div className="shrink-0 border-t border-ink-200 px-5 py-4 sm:px-7 dark:border-white/10">
              <div className="flex items-end justify-between gap-3">
                <div>
                  <p className="text-xs text-ink-500 dark:text-ink-400">One-time price</p>
                  <p className="mt-0.5 inline-flex items-center gap-1.5 text-2xl font-semibold tabular-nums text-ink-900 dark:text-white">
                    <MarqCoin className="h-6 w-6" />{price.toLocaleString()}
                    <span className="text-xs font-medium text-ink-400">KHARMA</span>
                  </p>
                </div>
                {user && balance !== null && (
                  <p className={'text-right text-xs ' + (short ? 'text-red-600 dark:text-red-400' : 'text-ink-500 dark:text-ink-400')}>
                    Balance {balance.toLocaleString()}
                    {short && <span className="block">Need {(price - balance).toLocaleString()} more</span>}
                  </p>
                )}
              </div>
              {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>}
              <button type="button" onClick={buy} disabled={busy} className={S_BTN_PRIMARY + ' mt-3 h-11 w-full text-[15px]'}>
                {!user ? 'Sign in to buy' : busy ? 'Purchasing…' : `Buy ${game.name}`}
              </button>
              <div className="mt-2 flex items-center justify-between text-xs text-ink-500 dark:text-ink-400">
                <button type="button" onClick={onClose} className="rounded px-1 py-1 hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-400 dark:hover:text-white">← All games</button>
                {user && short && <span>Top up with the daily reward in the header.</span>}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function fmtSecs(s: number) {
  const m = Math.floor(s / 60), r = Math.round(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
}

/**
 * The tutorial video, playing in the purchase dialog: muted autoplay so it
 * starts moving at once, a progress bar, and chapter chips that jump to each
 * beat (Shop · Fight · Boss · Reward …). A game without a recorded video shows
 * its live cover instead.
 */
function TutorialPlayer({ game }: { game: Game }) {
  const meta = GAME_VIDEOS[game.id];
  const ref = useRef<HTMLVideoElement>(null);
  const [t, setT] = useState(0);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);

  /*
   * Autoplay, for real. React never writes `muted` as an HTML attribute (only
   * the property, after mount), and browsers only autoplay a video that is
   * muted when it loads — so `autoPlay muted` alone left the purchase dialog on
   * the poster. Mute the element ourselves and start it, again once it can play
   * and whenever the tab comes back, unless the viewer paused it.
   */
  const userPaused = useRef(false);
  /*
   * Which file plays. H.264 MP4 first: it is hardware-decoded on every desktop
   * and phone, where the big VP9 WebM could stall or show garbage on some
   * Windows GPUs at dialog size (the hover tile hid it — it falls back to still
   * frames). The other format is the fallback: on an error, or when playback
   * makes no progress for a few seconds, the watchdog switches to it.
   */
  const formats = useRef<string[]>([]);
  const [src, setSrc] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);   // autoplay refused → a big Play button, never a frozen poster
  useEffect(() => {
    let alive = true;
    const probe = document.createElement('video');
    const order: [string, string][] = [['mp4', 'video/mp4; codecs="avc1.42E01E"'], ['webm', 'video/webm; codecs="vp9"']];
    const ok = order.filter(([, t]) => probe.canPlayType(t) !== '').map(([ext]) => `/games/videos/${game.id}.${ext}`);
    const net = ok.length ? ok : [`/games/videos/${game.id}.mp4`];
    userPaused.current = false;
    // An enabled game plays the copy stored in this browser (lib/videoCache) — no network at all.
    void localVideoUrl(game.id).then((local) => {
      if (!alive) return;
      formats.current = local ? [local, ...net] : net;
      setSrc(formats.current[0]);
    });
    return () => { alive = false; };
  }, [game.id]);
  const nextFormat = () => {
    const v = ref.current;
    const i = formats.current.indexOf(src ?? '');
    const alt = formats.current[i + 1];
    if (!v || !alt) return false;
    const at = v.currentTime || 0;
    setSrc(alt);
    requestAnimationFrame(() => { try { v.currentTime = at; } catch { /* starts at 0 */ } });
    return true;
  };
  useEffect(() => {
    const v = ref.current;
    if (!v || !src) return;
    v.defaultMuted = true;
    v.muted = muted;
    const start = () => {
      if (userPaused.current || !v.paused) return;
      v.play().then(() => setBlocked(false)).catch((e: Error) => { if (e?.name === 'NotAllowedError') setBlocked(true); });
    };
    v.load();
    start();
    v.addEventListener('canplay', start);
    const onVis = () => { if (!document.hidden) start(); };
    document.addEventListener('visibilitychange', onVis);
    // Watchdog: playing but the clock has not moved for 4 s → kick it, then switch format.
    let last = -1, stuck = 0;
    const dog = window.setInterval(() => {
      if (document.hidden || userPaused.current) { stuck = 0; return; }
      if (v.paused) { start(); return; }
      if (v.currentTime === last) {
        stuck++;
        if (stuck === 2) start();
        if (stuck >= 4) { stuck = 0; if (!nextFormat()) { v.load(); start(); } }
      } else stuck = 0;
      last = v.currentTime;
    }, 1000);
    return () => {
      v.removeEventListener('canplay', start);
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(dog);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-armed per source; `muted` has its own effect
  }, [src]);
  useEffect(() => { if (ref.current) ref.current.muted = muted; }, [muted]);

  if (!meta) {
    return (
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black">
        <GameShot game={game} live className="h-full w-full" />
      </div>
    );
  }
  const dur = meta.duration || 1;
  const chapters = meta.chapters ?? [];
  const at = chapters.reduce((k, c, i) => (t >= c.t ? i : k), 0);
  const seek = (s: number) => { const v = ref.current; if (v) { v.currentTime = s; void v.play().catch(() => {}); } };

  return (
    <div className="flex min-h-0 flex-col gap-3">
      <div className="relative mx-auto aspect-video w-full overflow-hidden rounded-lg bg-black shadow-2xl ring-1 ring-white/10"
           // As wide as the column, but never taller than the window leaves room for.
           style={{ maxWidth: 'calc((96vh - 13rem) * 1.7778)' }}>
        <video
          ref={ref}
          className="h-full w-full object-contain"
          poster={`/games/videos/${game.id}.jpg`}
          src={src ?? undefined}
          autoPlay
          muted={muted}
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          onTimeUpdate={(e) => setT(e.currentTarget.currentTime)}
          onPlay={() => { setPaused(false); setBlocked(false); }}
          onPause={() => setPaused(true)}
          onError={() => { nextFormat(); }}
        />
        {blocked && (
          <button
            type="button"
            onClick={() => { const v = ref.current; if (!v) return; userPaused.current = false; void v.play().then(() => setBlocked(false)).catch(() => {}); }}
            className="absolute inset-0 grid place-items-center bg-black/35 text-white"
            aria-label="Play the tutorial"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 via-orange-500 to-red-600 px-5 py-3 text-[15px] font-bold shadow-2xl">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden><path d="M8 5.5v13l11-6.5z" /></svg>
              Play the tutorial
            </span>
          </button>
        )}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/15">
          <div className="h-full bg-gradient-to-r from-amber-400 via-orange-500 to-red-600" style={{ width: `${Math.min(100, (t / dur) * 100)}%` }} />
        </div>
        <div className="absolute right-2 top-2 flex gap-1.5">
          <button type="button" onClick={() => { const v = ref.current; if (!v) return; userPaused.current = !v.paused; if (v.paused) void v.play(); else v.pause(); }}
                  aria-label={paused ? 'Play' : 'Pause'}
                  className="grid h-8 w-8 place-items-center rounded-full bg-black/55 text-white ring-1 ring-white/15 backdrop-blur-sm hover:bg-black/75">
            {paused
              ? <svg viewBox="0 0 24 24" className="ml-0.5 h-4 w-4" fill="currentColor" aria-hidden><path d="M8 5.5v13l11-6.5z" /></svg>
              : <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden><path d="M7 5h4v14H7zM13 5h4v14h-4z" /></svg>}
          </button>
          <button type="button" onClick={() => setMuted((m) => !m)} aria-label={muted ? 'Unmute' : 'Mute'}
                  className="grid h-8 w-8 place-items-center rounded-full bg-black/55 text-white ring-1 ring-white/15 backdrop-blur-sm hover:bg-black/75">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M4 9v6h4l5 4V5L8 9z" />{muted ? <path d="M17 9l5 6M22 9l-5 6" /> : <path d="M17 8a5 5 0 010 8" />}
            </svg>
          </button>
        </div>
      </div>
      {chapters.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chapters.map((c, i) => (
            <button
              key={c.label}
              type="button"
              onClick={() => seek(c.t)}
              className={
                'rounded-full px-3 py-1 text-[12px] font-semibold transition ' +
                (i === at
                  ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-ink-950 shadow'
                  : 'bg-white/10 text-white/75 hover:bg-white/20 hover:text-white')
              }
            >
              <span className="mr-1 tabular-nums opacity-70">{fmtSecs(c.t)}</span>{c.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** The game's in-game KHARMA items (server prices), for the purchase dialog's list. */
function useGameShopItems(gameId: string): { id: string; name: string; price: number }[] {
  const [items, setItems] = useState<{ id: string; name: string; price: number }[]>([]);
  useEffect(() => {
    let live = true;
    fetch('/api/marq/items', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!live || !j?.items) return;
        const out: { id: string; name: string; price: number }[] = [];
        for (const [id, it] of Object.entries(j.items as Record<string, { price: number; label: string }>)) {
          if (!id.startsWith(gameId + '.')) continue;
          // "Nova (Expert partner)" → "Nova · Expert": tier names fit the narrow tiles.
          const name = String(it.label).replace(/^[^·]*·\s*/, '').replace(/^(\w+) \((\w+) partner\)$/, '$1 · $2');
          out.push({ id, name: name.charAt(0).toUpperCase() + name.slice(1), price: Number(it.price) || 0 });
        }
        setItems(out);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [gameId]);
  return items;
}

/**
 * The game, full size, over the page.
 *
 * The frame is keyed on the game id and its src carries the theme, so switching
 * game — or theme — replaces the document rather than trying to talk to it. A
 * game has no API; reloading it is the API.
 */
/** Games that post `gt:game-ready` when their first screen has painted (the loader waits for it). */
const READY_SIGNAL = new Set(['contra', 'tanks', 'bounty', 'abyssal', ...READY_GAMES]);

function GameDialog({
  game,
  join,
  joinPw,
  onClose,
}: {
  game: Game;
  /** A room code from an invite link, handed to the page as `&join=`. */
  join?: string | null;
  /** That room's password, when the host put it in the invite link. */
  joinPw?: string | null;
  /** Step back down to the shelf — this game closes, the shelf is underneath. */
  onClose: () => void;
}) {
  const { resolved } = useTheme();
  const me = useAuth().user;
  const { stats, votes, favs } = useGameVotes(me?.id ?? null);
  const panel = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLIFrameElement>(null);

  /*
   * The loading screen over the frame (components/GameLoader). `fetch` until
   * the page has loaded; then a game that announces itself (READY_SIGNAL —
   * it posts `gt:game-ready` once its title has painted) stays on `boot`
   * until it does, and every other game is done at once. A safety timer never
   * leaves anyone staring at a loader.
   */
  const [phase, setPhase] = useState<LoadPhase>('fetch');
  const [loaderUp, setLoaderUp] = useState(true);
  useEffect(() => {
    setPhase('fetch'); setLoaderUp(true);
    const onReady = (e: Event) => { if ((e as CustomEvent).detail === game.id) setPhase('done'); };
    window.addEventListener('gt:game-ready', onReady);
    const hard = setTimeout(() => setPhase('done'), 30_000);
    return () => { window.removeEventListener('gt:game-ready', onReady); clearTimeout(hard); };
  }, [game.id]);
  useEffect(() => {
    if (phase !== 'boot') return;
    const t = setTimeout(() => setPhase('done'), 10_000);   // loaded, but the ready signal never came
    return () => clearTimeout(t);
  }, [phase]);
  const onFrameLoad = () => setPhase((p) => (p === 'done' ? p : READY_SIGNAL.has(game.id) ? 'boot' : 'done'));



  /**
   * Open the game's own menu, from outside it.
   *
   * The frame is same-origin, so this is a synthetic Escape dispatched on the
   * game's document — which reaches listeners on its `document` AND its
   * `window` (window is on every document event's path). Games opted in with
   * `escMenu` treat Escape as pause / menu. Focus goes back to the frame so the
   * next key the player presses lands in the game, not on this button.
   */
  const openGameMenu = () => {
    const w = frame.current?.contentWindow as (Window & typeof globalThis) | null | undefined;
    try {
      if (w) {
        const init = { key: 'Escape', code: 'Escape', keyCode: 27, which: 27, bubbles: true, cancelable: true };
        w.document.dispatchEvent(new w.KeyboardEvent('keydown', init));
        w.document.dispatchEvent(new w.KeyboardEvent('keyup', init));
      }
    } catch { /* a frame mid-navigation has no document yet */ }
    frame.current?.focus();
  };

  /*
   * onClose comes from the parent and is a new function on every render (a KHARMA
   * balance update re-renders the page). Read through a ref, so the effect below
   * runs once per game — before, every re-render re-ran it and its focus() took
   * the keyboard away from the game frame mid-match (Iron Talon online: the
   * guest's commando froze the moment the stake was spent).
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    /*
     * Escape here only fires when focus is OUTSIDE the game (the frame keeps
     * its own keys). For a game with a menu it still means "game menu", as the
     * chip says — closing the whole dialog on the same key would throw away a
     * run the player only wanted to pause. "All games" (top right) is the way
     * out, and a click on the backdrop still closes.
     */
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (game.escMenu) { e.preventDefault(); openGameMenu(); } else onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);

    /*
     * The page behind must not scroll while a game is up.
     *
     * Padding for the scrollbar's width as it is taken away, because the app
     * reserves that gutter permanently (see globals.css) and removing it
     * without compensating would shift the whole page 15px sideways at the
     * moment the dialog opens — exactly the twitch that gutter exists to stop.
     */
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    const prev = { overflow: document.body.style.overflow, pad: document.body.style.paddingRight };
    document.body.style.overflow = 'hidden';
    if (gutter > 0) document.body.style.paddingRight = `${gutter}px`;

    panel.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev.overflow;
      document.body.style.paddingRight = prev.pad;
    };
    // openGameMenu only reads refs, so it is safe to leave out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.id, game.escMenu]);

  /*
   * The first few seconds of every game: a card over the frame saying how to
   * get to the game's menu and how to get to another game — the two things
   * nothing inside a game can tell you about the dialog around it. Shown each
   * time a game opens (a new player needs it on the first game, and it costs
   * a returning one nothing), gone after five seconds or on a click.
   */
  const [hint, setHint] = useState(true);

  // A game left open PLAY_AFTER_MS counts as one play — what picks this
  // person's three Favourites. Switching away sooner is browsing, not playing.
  const signedIn = !!me;
  useEffect(() => {
    const t = setTimeout(() => recordPlay(game.id, signedIn), PLAY_AFTER_MS);
    return () => clearTimeout(t);
  }, [game.id, signedIn]);
  // And for as long as it stays open, the console can see who is in it.
  useGamePresence(game.id, signedIn);
  // The hint waits for the loading screen: five seconds of it from when the game is actually up.
  useEffect(() => {
    if (loaderUp) return;
    setHint(true);
    const t = setTimeout(() => setHint(false), 5000);
    return () => clearTimeout(t);
  }, [game.id, loaderUp]);

  // The keyboard belongs to the game the moment it is up: a player pressing the
  // arrow keys must not also be steering the page behind it.
  useEffect(() => {
    const t = setTimeout(() => frame.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [game.id]);

  return (
    <div
      // z-[100] to match the font dialog, and for the same reason: the language
      // dropdown is z-50 and later in the document, so equal z would lose.
      className="fixed inset-0 z-[100] flex"
      role="dialog"
      aria-modal="true"
      aria-label={game.name}
    >
      <div
        ref={panel}
        tabIndex={-1}
        // The whole window (owner, 2026-10-13): no margin of backdrop, so the
        // game gets every pixel there is. The way out is the bar — Close, or
        // "Games" in the trail — and both land on the shelf underneath.
        //
        // `gt-game-rotate`: on a phone held upright, the whole open game (bar and
        // frame) is turned 90° into landscape so the player turns the phone and
        // plays sideways (owner, 2026-10-14). It lives on this panel and nowhere
        // else, so it exists only while a game is open: closing the game
        // unmounts it and the shelf is upright again. See globals.css.
        className="gt-game-rotate flex h-[100dvh] w-full max-w-none flex-col overflow-hidden bg-white outline-none dark:bg-ink-900"
      >
        <GamesBar game={game} onClose={onClose} closeTitle="Close — back to all games">
          {/* The game's own menu: labelled with the key that opens it, and a
              button for anyone without one (or who never guessed it). */}
          {game.escMenu && (
            <button
              type="button"
              onClick={openGameMenu}
              title="Game menu (Esc) — pause, levels, main menu"
              aria-label="Open the game menu (Escape)"
              className="hidden h-9 shrink-0 items-center gap-1.5 rounded-full border border-ink-200 bg-ink-50 pl-1.5 pr-1.5 text-[12.5px] font-medium text-ink-700
                         transition hover:border-ink-400 hover:text-ink-900 sm:flex md:pr-3
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400
                         dark:border-white/15 dark:bg-white/[0.06] dark:text-white/85 dark:hover:border-white/40 dark:hover:text-white"
            >
              <kbd className="rounded-md border border-ink-300 bg-white px-1.5 py-px font-mono text-[10px] font-semibold text-ink-700 shadow-[0_1px_0_rgba(0,0,0,.15)] dark:border-white/25 dark:bg-white/10 dark:text-white/90 dark:shadow-[0_1px_0_rgba(0,0,0,.35)]">
                Esc
              </kbd>
              <span className="hidden md:inline">Game menu</span>
            </button>
          )}

          {/* ❤ favourite, then the like / dislike vote with everyone's live counts. */}
          <FavButton id={game.id} on={favs.includes(game.id)} />
          <VoteButtons id={game.id} stat={stats[game.id]} vote={votes[game.id]} big />
        </GamesBar>

        <div className="relative flex min-h-0 flex-1 flex-col">
        <iframe
          ref={frame}
          // `key` on the id: React reuses an <iframe> element across a src
          // change, and a reused frame keeps its history. A new element is a
          // new document, every time.
          key={game.id}
          src={`${game.file}?theme=${resolved === 'dark' ? 'dark' : 'light'}${join ? `&join=${encodeURIComponent(join)}${joinPw ? `&pw=${encodeURIComponent(joinPw)}` : ''}` : ''}`}
          title={game.name}
          // Same-origin so the frame can read its own localStorage for high
          // scores; scripts and touch. Popups (escaping the sandbox) so a news
          // card's "Read" can open the article in a normal new tab — see
          // Swarm·Crypto and /api/news. Nothing else it does not need.
          sandbox={`allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox${game.id === 'bounty' ? ' allow-downloads' : ''}`}
          // Room codes: Copy / Paste in the online lobby (Iron Talon's GTMatch).
          allow="clipboard-read; clipboard-write"
          onLoad={onFrameLoad}
          className="min-h-0 flex-1 border-0 bg-white dark:bg-ink-950"
        />
        {loaderUp && <GameLoader game={game} phase={phase} onGone={() => { setLoaderUp(false); frame.current?.focus(); }} />}
        {hint && !loaderUp && (
          <button
            type="button"
            onClick={() => { setHint(false); frame.current?.focus(); }}
            // At the TOP on a touch screen: the bottom of the frame is where
            // the games put their on-screen pad and buttons, and the hint sat
            // exactly on top of them.
            className="gt-game-hint absolute bottom-4 left-1/2 [@media(pointer:coarse)]:bottom-auto [@media(pointer:coarse)]:top-3 flex max-w-[92%] -translate-x-1/2 flex-wrap items-center justify-center
                       gap-x-3 gap-y-1 rounded-full bg-ink-900/85 px-4 py-2 text-[12px] text-white shadow-2xl ring-1 ring-white/15 backdrop-blur"
          >
            {game.escMenu && (
              // No Esc key on a phone.
              <span className="flex items-center gap-1.5 [@media(pointer:coarse)]:hidden">
                <kbd className="rounded-md border border-white/30 bg-white/10 px-1.5 font-mono text-[10px] font-semibold">Esc</kbd>
                game menu
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden>
                <path d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z" />
              </svg>
              <b className="font-semibold">Close</b> to pick another game
            </span>
          </button>
        )}
        </div>
      </div>
    </div>
  );
}
